// UI-2: Avatar, AvatarStack, StatusPill, EmptyState, Banner, Skeleton and ErrorState.
// design-brief.md "Avatars", "Status pills", "Empty states", "Alerts / banners".
import React from 'react';
import { v } from './tokens';
import { Button } from './Button';
import { PRIORITY_OPTIONS, PriorityDot } from './Priority';
import { mediaSrc } from '../utils/storedFiles';
import './v2.css';

export const AVATAR_SIZES = [24, 36, 44, 64];

export function initials(name) {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return '?';
  return (words[0][0] + (words.length > 1 ? words[words.length - 1][0] : '')).toUpperCase();
}

// size: 24 | 36 | 44 | 64. color: a pastel token. src: a photo, else initials.
export function Avatar({ name, src, size = 36, color = 'pastel-lilac', title }) {
  if (!AVATAR_SIZES.includes(size)) throw new Error(`Avatar: size must be one of ${AVATAR_SIZES.join(', ')}`);
  // A photo that will not load (removed since the list was fetched) falls back to the initials.
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => { setFailed(false); }, [src]);
  const photo = !failed && mediaSrc(src);
  return (
    <span className={`ui-avatar ui-avatar--${size}`} style={{ '--ui-bg': v(color) }} role="img" aria-label={title || name} title={title || name}>
      {photo ? <img src={photo} alt="" onError={() => setFailed(true)} /> : <span aria-hidden="true">{initials(name)}</span>}
    </span>
  );
}

// people: [{ name, color?, src? }]. Shows max, then "+N".
export function AvatarStack({ people, size = 36, max = 4 }) {
  const shown = people.slice(0, max);
  const more = people.length - shown.length;
  return (
    <span className={`ui-avatar-stack ui-avatar-stack--${size}`} role="group" aria-label={`${people.length} ${people.length === 1 ? 'person' : 'people'}`}>
      {shown.map((p) => <Avatar key={p.name} name={p.name} src={p.src} color={p.color} size={size} />)}
      {more > 0 && (
        <span className={`ui-avatar ui-avatar--${size} ui-avatar--more`} role="img" aria-label={`and ${more} more`}>
          <span aria-hidden="true">+{more}</span>
        </span>
      )}
    </span>
  );
}

export const PILL_TONES = ['success', 'pending', 'warning', 'neutral'];

// tone: success (Active, Paid) | pending (Sent, Trial) | warning (Overdue, Failed) | neutral (Draft).
// priority: 'low' | 'medium' | 'high' (decision 50), in place of a tone; the label defaults to the
// level's name. Text is always ink; the colour is never the only signal, the word is.
export function StatusPill({ tone = 'neutral', priority, children }) {
  if (priority !== undefined) {
    const p = PRIORITY_OPTIONS.find((o) => o.value === priority);
    if (!p) throw new Error(`StatusPill: unknown priority "${priority}"`);
    return (
      <span className="ui-pill ui-pill--priority" style={{ '--ui-bg': v(p.color) }}>
        {p.dot && <PriorityDot />}{children ?? p.label}
      </span>
    );
  }
  if (!PILL_TONES.includes(tone)) throw new Error(`StatusPill: unknown tone "${tone}"`);
  return <span className={`ui-pill ui-pill--${tone}`}>{children}</span>;
}

// A dashed box, one short sentence and one action. Never a bare "No data".
export function EmptyState({ icon, title, children, action }) {
  return (
    <div className="ui-empty">
      {icon && <span className="ui-empty__icon" aria-hidden="true">{icon}</span>}
      {title && <p className="ui-empty__title">{title}</p>}
      {children && <p className="ui-empty__text">{children}</p>}
      {action}
    </div>
  );
}

export const BANNER_TONES = ['problem', 'info', 'success', 'notice'];

// tone: problem (danger-bg) | info | success | notice. One sentence, one action, dismiss ×.
// A problem is role="alert" (announced at once); the others are role="status".
export function Banner({ tone = 'info', children, action, onDismiss, dismissLabel = 'Dismiss' }) {
  if (!BANNER_TONES.includes(tone)) throw new Error(`Banner: unknown tone "${tone}"`);
  return (
    <div className={`ui-banner ui-banner--${tone}`} role={tone === 'problem' ? 'alert' : 'status'}>
      <p className="ui-banner__text">{children}</p>
      {action}
      {onDismiss && (
        <button type="button" className="ui-banner__close ui-hit" aria-label={dismissLabel} onClick={onDismiss}>
          <span aria-hidden="true">✕</span>
        </button>
      )}
    </div>
  );
}

// A loading placeholder. lines: how many text bars; or a block of width x height.
export function Skeleton({ width = '100%', height = 14, lines, radius, label = 'Loading' }) {
  if (lines) {
    return (
      <span role="status" aria-label={label} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {Array.from({ length: lines }, (_, i) => (
          <span key={i} className="ui-skeleton" style={{ width: i === lines - 1 ? '60%' : '100%', height }} />
        ))}
      </span>
    );
  }
  return <span role="status" aria-label={label} className="ui-skeleton" style={{ width, height, borderRadius: radius }} />;
}

// When something failed to load: the server's own message, said plainly, and a retry.
// message: the real error text (messageFromError(err) at the call site), never a generic
// "Something went wrong" when the server said more.
export function ErrorState({ title = "This didn't load", message, onRetry, retryLabel = 'Try again', retrying = false }) {
  return (
    <div className="ui-error" role="alert">
      <p className="ui-error__title">{title}</p>
      {message && <p className="ui-error__message">{message}</p>}
      {onRetry && (
        <Button variant="secondary" size="sm" onClick={onRetry} disabled={retrying} aria-busy={retrying || undefined}>
          {retrying ? 'Trying again…' : retryLabel}
        </Button>
      )}
    </div>
  );
}
